import { Component } from '@components/Component/Component.js';
import { connectDOM } from '@components/Utility/Template.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { ReplaySubject, take } from 'rxjs';
import { forEachKey, getID } from '@components/Utility/index.js';
/**
 * Class `Container` extends the `Component` class.
 * It represents a container for different components and handles their lifecycle.
 */
export class Container extends Component {
    get childrenDrawn() { return this._childrenDrawn || false; }
    set childrenDrawn(value) {
        this._propertySet('childrenDrawn', value);
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Container';
        /** Object to store all the components where keys are component ids and values are component instances.*/
        this.components = {};
        this.rootElementType = 'div';
        this.componentSubjects = new Map();
        this.designerDragType = 'block';
        this.initializeProperties();
        this.initializeRecords();
        this.initializeComponents();
    }
    // if a subclass of container needs to set default properties prior to initializeRecords or initializeComponents is called
    initializeProperties() {
        this.lazyDrawContent = false;
        this._childrenDrawn = false;
        this.childTemplate = '';
    }
    /**
     * for framework container components that define their children internally such as LoginForm
     * @protected
     */
    initializeRecords() {
    }
    /**
     * for framework container components that define their children internally such as LoginForm
     * @protected
     */
    initializeComponents() {
    }
    /**
     * Method to add multiple components to the container.
     * @param {Component[]} components - Array of components to add to the container.
     */
    componentsAdd(components, appendToDom = false) {
        components.forEach((component) => {
            this.componentAdd(component, appendToDom);
        });
    }
    /**
     * Method to add a single component to the container.
     * @param {Component} component - Component to add to the container.
     * @param appendToDOM
     */
    componentAdd(component, appendToDOM = false) {
        this.components[component.id] = component;
        if (!component.containerId) {
            component.containerId = this.id;
        }
        if (!component.recordStore) {
            component.recordStore = this.recordStore;
        }
        if (!component.formContext) {
            component.formContext = this.formContext;
        }
        if (component.containerId === this.id) {
            component.globalContainerId = this.globalId;
        }
        if (this.record && !component.record) {
            if (this.recordName && !component.recordName) {
                component.recordName = this.recordName;
            }
            component.linkRecord(this.record);
        }
        if (appendToDOM && this.drawn) {
            this.appendChildComponent(component);
            if (!component.drawn && this.childrenDrawn) {
                component.draw();
            }
        }
        const subject = this.componentSubjects.get(component.id);
        if (subject) {
            subject.next(component);
        }
    }
    linkRecord(record) {
        super.linkRecord(record);
        for (const id in this.components) {
            const component = this.components[id];
            if (!component.record && !component.recordName) {
                component.recordName = this.recordName;
                component.linkRecord(record);
            }
        }
    }
    /**
     * Removes a component from this container and the DOM, does not destroy the component.
     * @param id
     * @param removeFromDOM
     */
    componentRemove(id, removeFromDOM = true) {
        if (this.components[id]) {
            this.components[id].containerId = '';
            if (removeFromDOM && this.components[id].rootElement) {
                this.components[id].rootElement.remove();
            }
            delete this.components[id];
        }
        this.componentSubjects.delete(id);
    }
    /**
     * Retrieve a component instance from the container by its id.
     * @param id
     *
     * @example
     * const titleInputComponent = form.componentGet('title');
     */
    componentGet(id) {
        return this.components[id];
    }
    componentsGet() {
        return this.components;
    }
    componentGet$(id) {
        let subject = this.componentSubjects.get(id);
        if (!subject) {
            subject = new ReplaySubject(1);
            this.componentSubjects.set(id, subject);
            const comp = this.componentGet(id);
            if (comp !== undefined) {
                subject.next(comp);
            }
        }
        return subject.asObservable().pipe(take(1));
    }
    draw() {
        super.draw();
        if (!this.lazyDrawContent) {
            this.drawChildren();
        }
    }
    /**
     * Alternative to built in "alert" for a styled alert box. Return an observable which emits when the alert is closed.
     * @example
     * // cover a form
     * form.alert({caption:'You do not have access.', title: 'some title'})
     * .subscribe(() => {
     *     // do something now that the user has clicked OK on the alert.
     * })
     *
     * // cover entire application
     * application.alert({caption: 'You do not have access.', title: 'some title'})
     * .subscribe(() => {
     *     // do something now that the user has clicked OK on the alert.
     * })
     *
     * @param options
     */
    alert(options) {
        const alert = DIContainer.new('Alert', getID('alert'), document.createElement('div'));
        alert.attachModal(this.rootElement);
        return alert.open(options);
    }
    /**
     * Alternative to built in "confirm" for a styled confirm box.
     * Returns an observable which emits when the confirm is approved (yes) and emits an error when declined (no).
     * @example
     * // cover a form
     * form.confirm({caption:'Are you sure?'})
     * .subscribe(() => {
     *     // do something now that the user has clicked YES on the confirm.
     * }, () => {
     *     // do something else now that the user has clicked NO on the confirm
     * })
     *
     * @param options
     */
    confirm(options) {
        const confirm = DIContainer.new('Confirm', getID('confirm'), document.createElement('div'));
        confirm.attachModal(this.rootElement);
        return confirm.open(options);
    }
    prompt(options) {
        const confirm = DIContainer.new('Prompt', getID('prompt'), document.createElement('div'));
        confirm.attachModal(this.rootElement);
        return confirm.open(options);
    }
    /**
     * to draw a specific child component outside of the normal flow (ie. delaying tab rendering until a tab is clicked).
     * @param id
     * @protected
     */
    lazyDrawChild(id) {
        if (this.components[id]) {
            const child = this.components[id];
            if (child.drawn) {
                return;
            }
            connectDOM(this.rootElement, {
                [id]: child
            });
        }
    }
    /**
     * draws the direct children of this container
     * @protected
     */
    drawChildren() {
        if (!this.childrenDrawn) {
            connectDOM(this.rootElement, this.components);
            this.childrenDrawn = true;
        }
    }
    /**
     * In cases where a template with nested root elements existed at a higher level such as a form
     * we need to extract the template from the root element before we insert the components actual template
     * and then re-insert that template that we found in the root element into the child container.
     * @protected
     */
    applyComponentTemplate() {
        let extractedNodes = [];
        if (this.rootElement.childNodes.length) {
            // we already have elements inside of our rootElement but we havent drawn our component yet.
            // so maybe someone defined a hardcoded html template for a form or something.
            // extract the "template" from rootElement
            extractedNodes = Array.from(this.rootElement.childNodes);
            this.rootElement.innerHTML = ''; // Clear the rootElement
        }
        // our template might include critical elements that are part of the container component that we'll need
        super.applyComponentTemplate();
        /*let template = this.template();
        if (template) {
            this.rootElement.innerHTML = template;
        }*/
        // now that we added the component template, we can find our childContainer
        let childContainer = this.childContainerGet();
        if (!childContainer) {
            childContainer = this.childContainer = this.rootElement;
        }
        if (extractedNodes.length) {
            // we need to re-insert the childNodes
            extractedNodes.forEach(node => childContainer.appendChild(node));
        }
        else if (childContainer.childNodes.length) {
            // this containers template included its child template.
            return;
        }
        else {
            // 
            const childTemplate = this.childTemplateGet();
            if (childTemplate) {
                childContainer.innerHTML = childTemplate;
            }
        }
    }
    /**
     * returns the child template property, or if that is not provided it returns a generated template.
     * @protected
     */
    childTemplateGet() {
        if (this.childTemplate) {
            return this.childTemplate;
        }
        // generate a childTemplate
        return this.generateChildTemplate();
    }
    /**
     * function for containers to setup a dynamic template based on their child components.
     * @protected
     */
    generateChildTemplate() {
        let template = '';
        if (this.compositeContainer) {
            // we need to sort the components first 
            const sortedComponents = [];
            for (var id in this.components) {
                sortedComponents.push(this.components[id]);
            }
            sortedComponents.sort((a, b) => {
                return a.seq - b.seq;
            });
            sortedComponents.forEach((component) => {
                const id = component.id;
                if (!this.components[id].ignoreChildTemplate && this.components[id].containerId === this.id) {
                    template += `<${this.components[id].rootElementType} data-componentid="${id}"></${this.components[id].rootElementType}>`;
                }
            });
        }
        else {
            for (var id in this.components) {
                if (!this.components[id].ignoreChildTemplate && this.components[id].containerId === this.id) {
                    template += `<${this.components[id].rootElementType} data-componentid="${id}"></${this.components[id].rootElementType}>`;
                }
            }
        }
        return template;
    }
    /**
     * function to return the DOM element that should contain the child components.
     * @protected
     */
    childContainerGet() {
        if (this.childContainer) {
            return this.childContainer;
        }
        this.childContainer = this.rootElement.querySelector('.child-container');
        return this.childContainer;
    }
    template() {
        return '';
    }
    appendChildComponent(childComponent) {
        if (!childComponent.rootElement) {
            let rootElement = this.childContainerGet().querySelector(`[data-componentid=${childComponent.id}]`);
            if (!rootElement) {
                rootElement = document.createElement(childComponent.rootElementType);
                rootElement.setAttribute('data-componentid', childComponent.id);
                this.childContainerGet().appendChild(rootElement);
            }
            childComponent.rootElement = rootElement;
        }
        else {
            this.childContainerGet().appendChild(childComponent.rootElement);
        }
    }
    shouldValidate() {
        if (this.hiddenState) {
            // if the entire container is hidden, there is no sense trying to enforce mandatory checks. User wouldnt be able to do anything.
            // this fixes an issue where form level modals (ie. invoice cash receipt) were enforcing mandatory on form save.
            return false;
        }
        return true;
    }
    /**
     * Validate form fields before saving against datadict length, mandatory flag (datadict), and required property.
     * @protected
     */
    validateFields(fieldsEncountered) {
        const mainRecord = this.record;
        let fullCaption = '';
        if (!this.shouldValidate()) {
            return '';
        }
        fieldsEncountered = fieldsEncountered || new Set();
        // for now simple validation only but will need to enhance for grids etc.
        // we can only technically validate fields that are visible and editable on the form or else the user wont have any way to proceed.
        forEachKey(this.components, (component) => {
            if (component.record === mainRecord
                && component.propertyGet('columnName')
                && !component.disabledState
                && !component.hiddenState
                && component.containerId === this.id // traverse from top to bottom, only validate direct childs.. this way if a container is hidden we skip it.
            ) {
                let value = mainRecord.get(component.propertyGet('columnName'));
                value = typeof value === 'number' ? value.toString() : value || '';
                const dataDict = component.propertyGet('dataDict');
                // eventually we might also support detail or secondary records here.. this fieldKey is a fix for a bug on customer form where 2x inputs for the same field duplicated in the fulllCaption
                const fieldKey = component.record.id + '.' + component.propertyGet('columnName');
                if ((component.propertyGet('required') || dataDict?.mandatoryFlag === 'Y')
                    && !value
                    && !fieldsEncountered.has(fieldKey)) {
                    fieldsEncountered.add(fieldKey);
                    const caption = this.CaptionManager.getCaption('system_requiredfield_caption', { field: component.propertyGet('labelCaption') || component.propertyGet('columnName') });
                    fullCaption += caption + '\n\n';
                }
            }
            // previously for "form" validating we just did everything linked to the form, but its safer if we just do the forms direct children and traverse down from them.
            if (component instanceof Container
                && component.containerId === this.id
            /*&& this.globalId !== this.formContext?.globalId*/ ) {
                fullCaption += component.validateFields(fieldsEncountered);
            }
        });
        return fullCaption;
    }
    destroy() {
        this.childContainer = undefined;
        this.componentSubjects.forEach((subject) => subject.complete());
        this.componentSubjects.clear();
        super.destroy();
        for (const key in this.components) {
            if (Reflect.has(this.components, key)) {
                const component = this.components[key];
                component.destroy();
                delete this.components[key];
            }
        }
    }
}
Container.VERSION = '$Revision: 127791 $';
